import React, { useEffect } from "react";
import makeStyles from "@mui/styles/makeStyles";
import PropTypes from "prop-types";
import { AppBar, Button } from "@mui/material";
import PdfViewer from "../PdfViewer/PdfViewer";
import {
  activityTypes,
  officeMimeTypesEncoded,
  pdfMimeType
} from "../../constants/index";
import GoogleDocsViewer from "../GoogleDocsViewer/GoogleDocsViewer";
import { connect } from "react-redux";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import AudioPlayer from "../AudioPlayer/AudioPlayer";
import VideoPlayer from "../VideoPlayer/VideoPlayer";
import CourseOverviewContainer from "./CourseOverview";
import { isIosSafari } from "../../utils/helpers";
import { validateContentViewerUrl } from "../../utils/validation";
import { useTranslation } from "react-i18next";

const iosFullScreenTopNavHeight = 40;

const videoStyles = makeStyles(() => ({
  container: {
    width: "100%"
  }
}));

const scormStyles = makeStyles(theme => ({
  container: {
    height: 670,
    marginBottom: theme.spacing(10)
  },
  iframe: {
    width: "100%",
    height: "100%"
  }
}));

const iframeStyles = makeStyles(theme => ({
  container: {
    position: "relative",
    paddingTop: "56.25%",
    marginBottom: theme.spacing(7),
    [theme.breakpoints.down("sm")]: {
      height: 670
    }
  },
  iframe: {
    position: "absolute",
    top: "0",
    left: "0",
    width: "100%",
    height: "100%"
  }
}));

const iosFullScreenStyles = makeStyles(() => ({
  container: {
    position: "fixed",
    height: `-webkit-fill-available`,
    backgroundColor: "white",
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    width: "100vw",
    zIndex: 3000,
    overflow: "hidden"
  },
  iframe: {
    position: "fixed",
    width: "100%",
    height: `calc(100% - ${iosFullScreenTopNavHeight}px)`
  },
  appBar: {
    minHeight: iosFullScreenTopNavHeight
  }
}));

const IosFullScreenHeader = ({ onClose }) => {
  const { t } = useTranslation();
  const classes = iosFullScreenStyles();
  return (
    <AppBar color="default" position="relative" className={classes.appBar}>
      <Button color="primary" onClick={onClose}>
        {t("contentViewer.navigationButtons.exitFullScreen")}
      </Button>
    </AppBar>
  );
};

const IFrameViewer = ({ iframeRef, contentLink, classes }) => {
  return (
    <iframe
      ref={iframeRef}
      allowFullScreen
      src={validateContentViewerUrl(contentLink)}
      frameBorder="0"
      title="iframe"
      className={classes.iframe}
    />
  );
};

function ContentPlayer(props) {
  const {
    contentLink,
    openInNewTab,
    mimeType,
    contentType,
    showSpinner,
    contentSettings,
    fullScreen,
    iosFullScreen,
    onFullScreenChange,
    onIosFullScreenChange,
    iframeRef,
    fileType,
    exitModalOpen,
    closeNewTabAfterOpeningLink
  } = props;

  const scormClasses = scormStyles();
  const iframeClasses = iframeStyles();
  const iosFullScreenClasses = iosFullScreenStyles();
  const videoClasses = videoStyles();

  useEffect(() => {
    if (fullScreen) {
      handleFullScreenClick();
    }
  }, [fullScreen]);

  const handleFullscreenChange = _ => {
    let fullscreen = false;
    if (
      document.fullscreenElement ||
      document.mozFullScreenElement ||
      document.webkitFullscreenElement ||
      document.msFullscreenElement ||
      document.fullscreen ||
      document.mozFullScreen ||
      document.webkitIsFullScreene ||
      document.fullScreenMode
    ) {
      fullscreen = true;
    }
    onFullScreenChange(fullscreen);
  };

  useEffect(() => {
    document.addEventListener(
      "webkitfullscreenchange",
      handleFullscreenChange,
      false
    );
    document.addEventListener(
      "mozfullscreenchange",
      handleFullscreenChange,
      false
    );
    document.addEventListener(
      "msfullscreenchange",
      handleFullscreenChange,
      false
    );
    document.addEventListener(
      "fullscreenchange",
      handleFullscreenChange,
      false
    );
    return () => {
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "mozfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "msfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    const bodyTag = document.getElementsByTagName("body")[0];
    if (bodyTag && iosFullScreen) {
      bodyTag.style = "overflow: hidden; ";
    } else if (!iosFullScreen && bodyTag.style.overflow === "hidden") {
      bodyTag.style.overflow = null;
    }
  }, [iosFullScreen]);

  useEffect(() => {
    if (!!contentLink && openInNewTab) {
      const win = window.open(contentLink, "_blank");
      if (win) {
        win.focus();
        if (closeNewTabAfterOpeningLink) window.close();
      }
    }
  }, [contentLink, openInNewTab]);

  function handleFullScreenClick() {
    if (isIosSafari()) {
      onIosFullScreenChange(true);
    } else if (contentType !== "video" && iframeRef.current) {
      let ref = iframeRef.current;
      if (ref.requestFullscreen) {
        ref.requestFullscreen();
      } else if (ref.mozRequestFullScreen) {
        /* Firefox */
        ref.mozRequestFullScreen();
      } else if (ref.webkitRequestFullscreen) {
        /* Chrome, Safari and Opera */
        ref.webkitRequestFullscreen();
      } else if (ref.msRequestFullscreen) {
        /* IE/Edge */
        ref.msRequestFullscreen();
      }
    }
  }

  const isOfficeFile = url => {
    return officeMimeTypesEncoded.some(url.includes.bind(url));
  };

  const isLocalFile = url => {
    return url.indexOf("localhost") > -1;
  };

  const isOfficeDoc =
    contentLink && isOfficeFile(contentLink) && !isLocalFile(contentLink);
  const isPdf = mimeType === pdfMimeType;
  const isJson =
    contentType === activityTypes.json &&
    contentSettings &&
    contentSettings.virtualClassroomSessionId;
  const isAudio = fileType && fileType.match("audio");
  const isVideo = contentType === "video";

  let classes = {};
  if (iosFullScreen) {
    classes = iosFullScreenClasses;
  } else if (isVideo || isAudio) {
    classes = videoClasses;
  } else if (contentType === activityTypes.scorm) {
    classes = scormClasses;
  } else if (!isPdf && !isOfficeDoc && !isJson && !isVideo && !isAudio) {
    classes = iframeClasses;
  }

  const getContent = () => {
    let content;
    if (isPdf) {
      content = <PdfViewer link={contentLink} key={contentLink} />;
    } else if (isOfficeDoc) {
      content = <GoogleDocsViewer url={contentLink} height="700px" />;
    } else if (isAudio) {
      content = <AudioPlayer src={contentLink} />;
    } else if (isVideo) {
      content = (
        <VideoPlayer
          mimeType={mimeType}
          contentLink={contentLink}
          fullScreen={fullScreen}
          key={contentLink}
        />
      );
    } else if (isJson) {
      content = (
        <CourseOverviewContainer
          fullPageLayout={false}
          match={{
            params: {
              assignedTrainingObjectId:
                contentSettings.virtualClassroomSessionId,
              versionId: contentSettings.virtualClassroomSessionId,
              virtualClassroomParentSession:
                contentSettings.virtualClassroomSessionParentId
            }
          }}
        />
      );
    } else if (openInNewTab) {
      content = null;
    } else {
      content = (
        <IFrameViewer
          iframeRef={iframeRef}
          contentLink={contentLink}
          classes={classes}
          key={contentLink}
        />
      );
    }
    return content;
  };

  const content = getContent();

  return (
    <>
      {showSpinner && <LoadingSpinner visible={showSpinner} />}

      <div className={classes.container}>
        {iosFullScreen && (
          <IosFullScreenHeader onClose={() => onIosFullScreenChange(false)} />
        )}
        {!exitModalOpen && content}
      </div>
    </>
  );
}

const mapStateToProps = state => ({
  showSpinner: state.contentPlayerState.showSpinner
});

export default connect(mapStateToProps)(ContentPlayer);

ContentPlayer.propTypes = {
  contentLink: PropTypes.string,
  contentType: PropTypes.string,
  fileType: PropTypes.string,
  downloadLink: PropTypes.string,
  navigationButtons: PropTypes.node
};
